/**
 * Dokumentzeile und ARKO-Leiste starten denselben Hintergrundlauf.
 * Ohne offenen Chat oder ohne schon gelesenen Auszug: kein Lauf, kein PDF-Weg.
 */
import type { PortalOpenDocumentMeta, PortalSelectedDocumentPayload } from './portalDocuments.ts';
import type { AkteDocContextCacheEntry, AkteExtractCacheEntry } from './portalAkteLazyExtract.ts';
import { hasUsableAkteExtract } from './portalAkteLazyExtract.ts';
import { preferFullExtractForAnalysis } from './portalBackgroundAnalysisChunks.ts';

export const DOCUMENT_ROW_ANALYSIS_LABEL = 'Im Hintergrund analysieren';

export type DocumentRowAnalysisBlock = 'no_chat' | 'no_text' | 'queue_full';

export type ReadableAnalysisDocument = {
  title: string;
  documentContext: string;
  fullExtractText?: string | null;
  documentExtractFingerprint?: string | null;
  extractPages?: number;
  extractRawChars?: number;
  extractTruncated?: boolean;
  extractSource?: string;
};

export type BackgroundAnalysisEnqueueArgs = {
  documentId: string;
  documentName: string;
  threadId: string;
  documentContext: string;
  openDocumentsMeta?: PortalOpenDocumentMeta[];
  selectedDocuments?: PortalSelectedDocumentPayload[];
  documentExtractFingerprint?: string | null;
  extractPages?: number;
  extractRawChars?: number;
  extractTruncated?: boolean;
  extractSource?: string;
};

export function analysisDocumentIdFromPbRecord(pbRecordId: string): string {
  const id = pbRecordId.trim();
  return id ? `pb:${id}` : '';
}

export function documentRowAnalysisBlock(args: {
  threadId?: string | null;
  documentContext?: string | null;
  canEnqueue: boolean;
}): DocumentRowAnalysisBlock | null {
  if (!args.threadId?.trim()) return 'no_chat';
  if (!hasUsableAkteExtract(args.documentContext)) return 'no_text';
  if (!args.canEnqueue) return 'queue_full';
  return null;
}

export function documentRowAnalysisBlockTitle(block: DocumentRowAnalysisBlock): string {
  if (block === 'no_chat') return 'Kein offener Chat';
  if (block === 'no_text') return 'Dokumenttext wird noch geladen';
  return 'Maximal zwei Hintergrundanalysen gleichzeitig';
}

/** Schon gelesener Auszug. Kein Fetch, kein zweiter Text. */
export function readableAnalysisDocument(args: {
  documentId: string;
  live?: AkteDocContextCacheEntry | null;
  persisted?: AkteExtractCacheEntry | null;
}): ReadableAnalysisDocument | null {
  const documentId = args.documentId.trim();
  if (!documentId) return null;
  const live = args.live;
  if (live && hasUsableAkteExtract(live.documentContext)) {
    return {
      title: live.title?.trim() || documentId,
      documentContext: live.documentContext!.trim(),
      fullExtractText: live.fullExtractText,
      documentExtractFingerprint: live.documentExtractFingerprint,
      extractPages: live.extractPages ?? undefined,
      extractRawChars: live.extractRawChars ?? undefined,
      extractTruncated: live.extractTruncated,
      extractSource: live.extractSource ?? undefined,
    };
  }
  const persisted = args.persisted;
  if (
    persisted
    && persisted.documentId.trim() === documentId
    && hasUsableAkteExtract(persisted.documentContext)
  ) {
    return {
      title: persisted.title?.trim() || documentId,
      documentContext: persisted.documentContext.trim(),
      fullExtractText: persisted.fullExtractText,
      documentExtractFingerprint: persisted.fingerprint,
      extractPages: persisted.extractPages,
      extractRawChars: persisted.extractRawChars,
      extractTruncated: persisted.extractTruncated,
      extractSource: persisted.extractSource,
    };
  }
  return null;
}

/**
 * Derselbe Start wie die ARKO-Leiste: Warteschlange, frisches Fenster, Webhook.
 * Gibt false zurück, ohne enqueue, wenn Chat, Text oder Platz fehlt.
 */
export function startBackgroundDocumentAnalysis(
  args: {
    threadId?: string | null;
    documentId?: string | null;
    documentName?: string | null;
    documentContext?: string | null;
    fullExtractText?: string | null;
    canEnqueue: boolean;
    openDocumentsMeta?: PortalOpenDocumentMeta[];
    documentExtractFingerprint?: string | null;
    extractPages?: number;
    extractRawChars?: number;
    extractTruncated?: boolean;
    extractSource?: string;
  },
  enqueue: (job: BackgroundAnalysisEnqueueArgs) => void,
): boolean {
  const threadId = args.threadId?.trim() || '';
  const documentId = args.documentId?.trim() || '';
  if (!threadId || !documentId) return false;
  if (documentRowAnalysisBlock({
    threadId,
    documentContext: args.documentContext,
    canEnqueue: args.canEnqueue,
  })) {
    return false;
  }
  const raw = args.documentContext!.trim();
  const preferred = preferFullExtractForAnalysis(raw, args.fullExtractText);
  const documentContext = hasUsableAkteExtract(preferred) ? preferred : raw;
  enqueue({
    documentId,
    documentName: args.documentName?.trim() || 'Dokument',
    threadId,
    documentContext,
    openDocumentsMeta: args.openDocumentsMeta,
    documentExtractFingerprint: args.documentExtractFingerprint,
    extractPages: args.extractPages,
    extractRawChars: args.extractRawChars,
    extractTruncated: args.extractTruncated,
    extractSource: args.extractSource,
  });
  return true;
}
